import { showToast } from 'common';
import {getDomainGraphqlError} from 'graphqlsupport';
import {useAsyncState} from 'placement/hook/useAsyncState';
import {useMatcheOffer} from 'placement/hook/useMatchOffer';
import {ReferralPageAction} from 'placement/reducers/referralPage';
import pluralize from 'pluralize';
import React from 'react';

type Props = {
    showModal: boolean;
    selectedOfferGuids: Array<string>
    dispatch: React.Dispatch<ReferralPageAction>
}

export function MatchOffersModal({
                                     showModal, dispatch, selectedOfferGuids
                                 }: Props) {
    const [loading, setLoading] = useAsyncState<boolean>(false);
    const {execute} = useMatcheOffer();

    const modalRef = React.useRef(null);
    let loadingTextCss = '';
    let confirmText = 'Yes, Match';
    if (loading) {
        loadingTextCss = 'loading-text';
        confirmText = 'Loading';
    }

    const hideModal = React.useCallback(() => {
        if (!loading) {
            dispatch({type: 'SET_HIDE_MATCH_MODAL'});
        }
    }, [loading, dispatch])

    const escFunction = React.useCallback((event) => {
        if (event.keyCode === 27) {
            hideModal()
        }
    }, [hideModal]);

    React.useEffect(() => {
        document.addEventListener('keydown', escFunction, false);

        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [escFunction]);

    React.useEffect(() => {
        // close modal on outside modal click
        const handleClickOutside = (e: Event) => {
            if (modalRef && (modalRef.current as any).contains(e.target)) {
                // inside
                return;
            }
            hideModal();
        };
        if (showModal) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showModal, hideModal]);

    const matchOffers = async () => {
        setLoading(true);
        let haveError = false;
        try {
            await execute({variables: {offerGuids: selectedOfferGuids}});
        } catch (e) {
            console.log(e);
            haveError = true;
            let errMessage = getDomainGraphqlError(e)
            if (!errMessage) {
                errMessage = 'Something went wrong when trying to match referrals';
            }
            showToast.error(errMessage);
        }
        if (!haveError) {
            showToast.success(`Matched ${selectedOfferGuids.length} ${pluralize('referral', selectedOfferGuids.length)} with the providers`);
        }
        setLoading(false);
        dispatch({type: 'SET_MATCH_OFFER_DONE'})
    }

    return (
        <React.Fragment>
            {
                showModal ? (
                    <>
                        <div
                            className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
                            <div className='relative w-auto my-6 mx-auto max-w-3xl'>
                                <div
                                    className='border-0 rounded shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                                    <div
                                        className='flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t'>
                                        <h3 className='text-base font-semibold'>
                                            Match Providers
                                        </h3>
                                    </div>
                                    <div className='relative p-6 flex-auto'>
                                        <p className='my-4 text-sm text-gray-600 leading-relaxed'>
                                            Are you sure you want to
                                            match {pluralize('this', selectedOfferGuids.length)} {selectedOfferGuids.length} {pluralize('referral', selectedOfferGuids.length)} with
                                            the providers?
                                        </p>
                                    </div>
                                    <div ref={modalRef}
                                         className='flex items-center justify-center p-6 border-t border-solid border-gray-300 rounded-b'>
                                        <button
                                            className='text-atlantis-200 background-transparent px-10 py-3 text-sm outline-none focus:outline-none mr-4 mb-1 border border-atlantis-200 rounded'
                                            type='button'
                                            onClick={hideModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={`bg-atlantis-200 text-white active:bg-atlantis-300 font-semibold text-sm px-10 py-3 shadow hover:shadow-lg
                                                    outline-none focus:outline-none mr-1 mb-1 rounded ${loadingTextCss}`}
                                            type='button'
                                            style={{transition: 'all .15s ease'}}
                                            onClick={matchOffers}
                                            data-testid="match-offers-confirm"
                                        >
                                            {confirmText}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
                    </>
                ) : null
            }
        </React.Fragment>
    );
}
